import React, { ChangeEvent } from 'react';
import { InputSelect, InputText } from '@lambdacurry/component-library';
import { AccessibleCompany } from '../../../types';
import { AuditLogFilters as AuditLogFiltersModel } from '../../../types/AuditLogs';

interface AuditLogFiltersProps {
    accessibleCompanies: AccessibleCompany[];
    filters: AuditLogFiltersModel;
    actionCategoryOptions: string[];
    selectedCompanyId?: number;
    showCompanySelector: boolean;
    canExport: boolean;
    onCompanyChange: (companyId?: number) => void;
    onFiltersChange: (filters: AuditLogFiltersModel) => void;
    onResetFilters: () => void;
    onExportCsv: () => void;
}

export const AuditLogFilters = ({
    accessibleCompanies,
    filters,
    actionCategoryOptions,
    selectedCompanyId,
    showCompanySelector,
    canExport,
    onCompanyChange,
    onFiltersChange,
    onResetFilters,
    onExportCsv
}: AuditLogFiltersProps) => {
    const updateTextFilter = (field: keyof AuditLogFiltersModel) => (event: ChangeEvent<HTMLInputElement>) => {
        onFiltersChange({
            ...filters,
            [field]: event.target.value
        } as AuditLogFiltersModel);
    };

    const updateSelectFilter = (field: keyof AuditLogFiltersModel) => (_event: ChangeEvent<any>, value: string) => {
        onFiltersChange({
            ...filters,
            [field]: value
        } as AuditLogFiltersModel);
    };

    const companyOptions = [
        { label: 'All companies', value: '' },
        ...accessibleCompanies.map(company => ({
            label: `${company.displayName || company.name} (#${company.id})`,
            value: company.id
        }))
    ];
    const actionCategorySelectOptions = [
        { label: 'All action categories', value: 'all' },
        ...actionCategoryOptions.map(actionCategory => ({
            label: actionCategory,
            value: actionCategory
        }))
    ];

    return (
        <section className="audit-log-toolbar">
            {showCompanySelector ? (
                <div className="audit-log-field audit-log-field-company">
                    <InputSelect
                        id="audit-log-company"
                        label="Company / Property"
                        name="audit-log-company"
                        options={companyOptions}
                        optionLabelKey="label"
                        optionValueKey="value"
                        value={selectedCompanyId || ''}
                        placeholder="All companies"
                        onChange={(_event, value) => onCompanyChange(value ? Number(value) : undefined)}
                        autocompleteConfig={{ disableClearable: false }}
                        inputProps={{ autoComplete: 'off' }}
                    />
                </div>
            ) : null}

            <div className="audit-log-filters-grid">
                <div className="audit-log-field audit-log-field-search">
                    <InputText
                        id="audit-log-actor-email"
                        label="Actor (User Email)"
                        name="audit-log-actor-email"
                        type="search"
                        placeholder="Filter by actor email"
                        value={filters.actorEmail || ''}
                        onChange={updateTextFilter('actorEmail')}
                    />
                </div>

                <div className="audit-log-field audit-log-field-search">
                    <InputText
                        id="audit-log-target"
                        label="Client / Property"
                        name="audit-log-target"
                        type="search"
                        placeholder="Filter by client, property, or ID"
                        value={filters.targetText || ''}
                        onChange={updateTextFilter('targetText')}
                    />
                </div>

                <div className="audit-log-field">
                    <InputSelect
                        id="audit-log-action-category"
                        label="Action Category"
                        name="audit-log-action-category"
                        options={actionCategorySelectOptions}
                        optionLabelKey="label"
                        optionValueKey="value"
                        value={filters.actionCategory || 'all'}
                        onChange={updateSelectFilter('actionCategory')}
                        inputProps={{ autoComplete: 'off' }}
                    />
                </div>

                <div className="audit-log-field">
                    <InputText
                        id="audit-log-date-from"
                        label="Date from"
                        name="audit-log-date-from"
                        type="date"
                        value={filters.dateFrom || ''}
                        onChange={updateTextFilter('dateFrom')}
                        InputLabelProps={{ shrink: true }}
                    />
                </div>

                <div className="audit-log-field">
                    <InputText
                        id="audit-log-date-to"
                        label="Date to"
                        name="audit-log-date-to"
                        type="date"
                        value={filters.dateTo || ''}
                        onChange={updateTextFilter('dateTo')}
                        InputLabelProps={{ shrink: true }}
                    />
                </div>

                <div className="audit-log-field audit-log-field-actions">
                    <button type="button" className="audit-log-reset-button" onClick={onResetFilters}>
                        Clear filters
                    </button>
                </div>

                <div className="audit-log-field audit-log-field-actions">
                    <button
                        type="button"
                        className="audit-log-export-button"
                        onClick={onExportCsv}
                        disabled={!canExport}
                    >
                        Export to CSV
                    </button>
                </div>
            </div>
        </section>
    );
};
